/**
 * Hooks for the Playlist ("Performance") domain.
 *
 * Replaces Wized requests: get_playlist, get_playlist_item, get_playlist_courses,
 *   get_playlist_courses_videos, create_playlist, delete_playlist,
 *   Add_to_playlist, update_playlist_order
 * Replaces Wized elements: playlist-item, playlist-name, go_to_playlist,
 *   playlist_list, playlist_name, playlist_course-item, current_playlist_name,
 *   current_playlist_videos_count, reorder_playlist_course-item, reorder_submit, etc.
 */

import { useMemo } from "react";
import { useAsync, useMutation } from "./useAsync";
import * as api from "../lib/api";
import { useAuth } from "../lib/auth";
import type { PlaylistCourseWithDetails } from "../lib/schema";

/** Fetch all playlists for the current user. */
export function usePlaylists() {
  const { user } = useAuth();
  return useAsync(
    () => api.getPlaylists(user!.id),
    user ? [user.id] : null,
  );
}

/** Fetch a single playlist by ID. */
export function usePlaylistItem(playlistId: string | null) {
  return useAsync(
    () => api.getPlaylistItem(playlistId!),
    playlistId ? [playlistId] : null,
  );
}

/** Fetch all playlist-course entries. */
export function usePlaylistCourses() {
  return useAsync(() => api.getPlaylistCourses(), []);
}

/** Fetch playlist-course entries for a single playlist. */
export function usePlaylistCoursesForPlaylist(playlistId: string | null) {
  return useAsync(
    () => api.getPlaylistCoursesForPlaylist(playlistId!),
    playlistId ? [playlistId] : null,
  );
}

/** Fetch playlist courses with video-level detail (for shared pages). */
export function usePlaylistCoursesVideos() {
  return useAsync(() => api.getPlaylistCoursesVideos(), []);
}

/** Mutation: create a new playlist. */
export function useCreatePlaylist() {
  const { user } = useAuth();
  return useMutation(async (name: string) => {
    if (!user) return { data: null, error: { message: "Not authenticated" } };
    return api.createPlaylist(user.id, name);
  });
}

/** Mutation: rename a playlist. */
export function useUpdatePlaylistName() {
  const { user } = useAuth();
  return useMutation(async (playlistId: string, name: string) => {
    if (!user) return { data: null, error: { message: "Not authenticated" } };
    return api.updatePlaylistName(user.id, playlistId, name);
  });
}

/** Mutation: delete a playlist. */
export function useDeletePlaylist() {
  return useMutation(async (playlistId: string) => {
    return api.deletePlaylist(playlistId);
  });
}

/** Mutation: add a course to a playlist. */
export function useAddCourseToPlaylist() {
  return useMutation(async (playlistId: string, courseId: string) => {
    return api.addCourseToPlaylist(playlistId, courseId);
  });
}

/** Mutation: update notes on a playlist course. */
export function useUpdatePlaylistCourseNotes() {
  return useMutation(
    async (playlistCourseId: string, notes: string | null) => {
      return api.updatePlaylistCourseNotes(playlistCourseId, notes);
    },
  );
}

/** Mutation: remove a course from a playlist. */
export function useRemoveCourseFromPlaylist() {
  return useMutation(async (playlistCourseId: string) => {
    return api.removeCourseFromPlaylist(playlistCourseId);
  });
}

/** Mutation: reorder playlist courses. */
export function useUpdatePlaylistOrder() {
  return useMutation(async (playlistId: string, courseIds: string[]) => {
    return api.updatePlaylistOrder(playlistId, courseIds);
  });
}

/** Mutation: update the music_url on a playlist. */
export function useUpdatePlaylistMusicUrl() {
  return useMutation(async (playlistId: string, musicUrl: string | null) => {
    return api.updatePlaylistMusicUrl(playlistId, musicUrl);
  });
}

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/** Filter playlist courses belonging to a specific playlist. */
export function useFilteredPlaylistCourses(
  playlistCourses: PlaylistCourseWithDetails[] | null,
  playlistId: string | null,
): PlaylistCourseWithDetails[] {
  return useMemo(() => {
    if (!playlistCourses || !playlistId) return [];
    return playlistCourses.filter(
      (pc) => pc.playlist_id.id === playlistId,
    );
  }, [playlistCourses, playlistId]);
}

/** Check if a course is already in a specific playlist. */
export function isCourseInPlaylist(
  playlistCourses: PlaylistCourseWithDetails[] | null,
  courseId: string,
  playlistId: string,
): boolean {
  if (!playlistCourses) return false;
  return playlistCourses.some(
    (pc) => pc.course_id.id === courseId && pc.playlist_id.id === playlistId,
  );
}
